import coralApi from '../../api/coral-api';
import {
  transformGroupFromApi,
  transformParticipantFromApi,
  transformParticipantToApi,
  transformMessageFromApi,
  transformAlertFromApi,
  handleApiError,
} from './coral-utils';

class CoralService {
  // Groups
  async getGroups(organizationId, filter = "all") {
    try {
      const response = await coralApi.fetchGroups(organizationId, filter);
      const groups = Array.isArray(response) ? response : response.groups || [];
      return groups.map(transformGroupFromApi);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async deleteGroup(organizationId, groupId) {
    try {
      const response = await coralApi.deleteGroup(organizationId, groupId);
      return response;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getGroup(organizationId, groupId) {
    try {
      const group = await coralApi.fetchGroup(organizationId, groupId);
      return transformGroupFromApi(group);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async createGroup(organizationId, groupData) {
    try {
      const payload = {
        subject: groupData.name || groupData.subject,
        description: groupData.description || '',
        participants: groupData.members?.map(transformParticipantToApi) ||
                     groupData.participants || [],
        isThreaded: groupData.isAnonymous,
      };

      const response = await coralApi.createGroup(organizationId, payload);
      return transformGroupFromApi(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async editGroup(organizationId, groupId, groupData) {
    try {
      let payload = {
        name: groupData.name || groupData.subject, // may be called subject (if using Coral docs)
        description: groupData.description || '',
      };

      if (groupData.emailRecipients != null) {
        payload.emailRecipients = groupData.emailRecipients;
      }

      if (groupData.emailOnEachMessage != null) {
        payload.emailOnEachMessage = groupData.emailOnEachMessage;
      }

      const response = await coralApi.editGroup(organizationId, groupId, payload);
      return transformGroupFromApi(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Participants
  async getGroupParticipants(organizationId, groupId) {
    try {
      const response = await coralApi.fetchGroupParticipants(organizationId, groupId);
      const participants = Array.isArray(response) ? response : response.participants || [];
      return participants.map(transformParticipantFromApi);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async addGroupParticipants(organizationId, groupId, participants) {
    try {
      const formattedParticipants = participants.map(transformParticipantToApi);
      const response = await coralApi.addGroupParticipants(organizationId, groupId, formattedParticipants);
      return response;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async removeGroupParticipant(organizationId, groupId, phoneNumber) {
    try {
      const phoneNumbers = Array.isArray(phoneNumber) ? phoneNumber : [phoneNumber];
      const response = await coralApi.removeGroupParticipants(organizationId, groupId, phoneNumbers);
      return response;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Messages
  async getGroupMessages(organizationId, groupId, page = 1) {
    try {
      const response = await coralApi.fetchGroupMessages(organizationId, groupId, page);
      const messages = Array.isArray(response) ? response : response.items || response.messages || [];
      const total = response.total || 0;
      const limit = response.limit || 50;
      const currentPage = response.page || page;
      const totalPages = Math.ceil(total / limit);

      return {
        messages: messages.map(transformMessageFromApi),
        hasMore: currentPage < totalPages,
        page: currentPage,
        totalPages: totalPages,
      };
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async sendMessage(organizationId, groupId, message) {
    try {
      const response = await coralApi.sendGroupMessage(organizationId, groupId, message);
      return transformMessageFromApi(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Templates
  async sendTemplate(organizationId, groupId, templateName, parameters) {
    try {
      const response = await coralApi.sendGroupTemplate(organizationId, groupId, templateName, parameters);
      return transformMessageFromApi(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Alerts
  async getAllAlerts(organizationId) {
    try {
      const response = await coralApi.fetchAllAlerts(organizationId);
      const alerts = Array.isArray(response) ? response : response.alerts || [];
      return alerts.map(transformAlertFromApi);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getAlert(organizationId, alertId) {
    try {
      const response = await coralApi.fetchAlert(organizationId, alertId);
      return transformAlertFromApi(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getGroupAlerts(organizationId, groupId) {
    try {
      const response = await coralApi.fetchGroupAlerts(organizationId, groupId);
      const alerts = Array.isArray(response) ? response : response.alerts || [];
      return alerts.map(transformAlertFromApi);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async sendAlertEmail(organizationId, alertId, recipientEmail, subject, body) {
    try {
      const response = await coralApi.sendAlertEmail(organizationId, alertId, recipientEmail, subject, body);
      return response;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Organization settings
  async getOrganizationSettings(organizationId) {
    try {
      const response = await coralApi.fetchOrganizationSettings(organizationId);
      return response;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
  
  async updateOrganizationSettings(organizationId, organizationSettings) {
    try {
      const response = await coralApi.updateOrganizationSettings(organizationId, organizationSettings);
      return response;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Composite operations
  async getGroupWithDetails(organizationId, groupId) {
    try {
      const [group, participants, messages, alerts] = await Promise.all([
        this.getGroup(organizationId, groupId),
        this.getGroupParticipants(organizationId, groupId),
        this.getGroupMessages(organizationId, groupId, 1),
        this.getGroupAlerts(organizationId, groupId),
      ]);

      return {
        ...group,
        members: participants.participants,
        invitees: participants.invitees,
        messages: messages.messages,
        alerts,
      };
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
}

export default new CoralService();